import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from "uuid";

import {
  Add,
  DeleteForever,
} from "@mui/icons-material";
import { Tooltip, Fab } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { FeatureSource, ModelBase } from "@models";
import { 
  Condition, 
} from "@dndModels";
import { CONDITION_URLS } from '../../assets/img/conditions';

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import StringBox from "@components/input/strings/StringBox";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";

import FeatureSourceInput from "@dndComponents/FeatureSourceInput";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import SelectStringBox from "@components/input/selects/SelectStringBox";

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

interface Props {
  obj?: ModelBase;
}

export default function ConditionEdit({ obj: param_obj }: Props) {
  const [reloading, setReloading] = useState(false);
  const [obj, setObj] = useState<Condition>(param_obj instanceof Condition ? param_obj : api.applyWorkingOn(new Condition()));
  const [change_time, setChangeTime] = useState(0);
  
  const disabled = api.user === null || !api.online || (!api.user.admin && obj._id !== "" && obj.owner_id !== api.user._id);
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading]);

  const level_options = ["0"];
  for (let i = 1; i <= obj.max_level; ++i) {
    level_options.push(`${i}`);
  }
  return (
    <FeatureSourceInput 
      obj={obj}
      data_type="condition"
      disabled={disabled}
      change_time={change_time}
      image_options={CONDITION_URLS}
      onChange={(changed: FeatureSource) => {
        if (changed instanceof Condition) {
          const new_obj = obj;
          new_obj.copy(changed);
          setObj(new_obj);
        }
      }}
    >
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 12
          }}>
          { obj.max_level > 1 ?
            <SelectStringBox
              name="Reduce on Long Rest"
              value={`${obj.reduce_on_long_rest}`}
              options={level_options}
              disabled={disabled}
              onChange={(changed: string) => {
                const new_obj = obj;
                new_obj.reduce_on_long_rest = +changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
            : 
            <ToggleButtonBox 
              name="Remove on Long Rest"
              value={obj.reduce_on_long_rest > 0}
              disabled={disabled}
              onToggle={() => {
                const new_obj = obj;
                new_obj.reduce_on_long_rest = obj.reduce_on_long_rest > 0 ? 0 : 1;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          }
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <ToggleButtonBox 
            name="Immunity Exists"
            value={obj.immunity_exists} 
            disabled={disabled}
            onToggle={() => {
              const new_obj = obj;
              new_obj.immunity_exists = !obj.immunity_exists;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <ToggleButtonBox 
            name="Common"
            value={obj.common} 
            disabled={disabled}
            onToggle={() => {
              const new_obj = obj;
              new_obj.common = !obj.common;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        { !obj.common &&
          <>
            <Grid
              size={{
                xs: 12
              }}>
              <SelectObjectBox
                name="Common for these Classes" 
                data_type="game_class"
                values={ obj.class_ids } 
                multiple
                disabled={disabled}
                onChange={(ids: string[]) => {
                  const new_obj = obj;
                  new_obj.class_ids = ids;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }} 
              />
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>
              <SelectObjectBox
                name="Common for these Subclasses" 
                data_type="subclass"
                filter={ obj.class_ids.length === 1 ? { game_class_id: obj.class_ids[0]} : undefined }
                values={ obj.subclass_ids } 
                multiple
                disabled={disabled}
                // log_it
                onChange={(ids: string[]) => {
                  const new_obj = obj;
                  new_obj.subclass_ids = ids;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }} 
              />
            </Grid>
          </>
        }
        <Grid
          size={{
            xs: 12
          }}>
          <ToggleButtonBox 
            name="Is an Exhaustion Condition"
            value={obj.exhaustion} 
            disabled={disabled}
            onToggle={() => {
              const new_obj = obj;
              new_obj.exhaustion = !obj.exhaustion;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <ToggleButtonBox 
            name="Is an Encumbrance Condition"
            value={obj.encumbrance} 
            disabled={disabled}
            onToggle={() => {
              const new_obj = obj;
              new_obj.encumbrance = !obj.encumbrance;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        { obj.encumbrance ?
          <Grid
            container
            spacing={0}
            direction="column"
            size={{
              xs: 12
            }}>
            <Grid>
              <Tooltip title={`Add New Encumbrance Level`}>
                <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                  disabled={disabled}
                  onClick={ () => {
                    const new_obj = obj;
                    new_obj.encumbrance_levels.push({
                      threshold: 0,
                      name: "New Encumbrance Level",
                      color: "",
                      true_id: uuidv4().toString()
                    });
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}>
                  <Add/>
                </Fab>
              </Tooltip>
            </Grid>
            { !reloading && obj.encumbrance_levels.map((level, key) => {
              return (
                <Grid key={key} container spacing={0} direction="row">
                  <Grid
                    size={{
                      xs: 3
                    }}>
                    <StringBox 
                      name="Name"
                      value={`${level.name}`} 
                      disabled={disabled}
                      onBlur={(changed: string) => {
                        const new_obj = obj;
                        const level_finder = new_obj.encumbrance_levels.filter(o => o.true_id === level.true_id);
                        if (level_finder.length === 1) {
                          level_finder[0].name = changed;
                        }
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                      }}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 3
                    }}>
                    <StringBox 
                      name="Threshold"
                      value={`${level.threshold}`} 
                      type="number"
                      disabled={disabled}
                      onBlur={(changed: string) => {
                        const new_obj = obj;
                        const level_finder = new_obj.encumbrance_levels.filter(o => o.true_id === level.true_id);
                        if (level_finder.length === 1) {
                          level_finder[0].threshold = +changed;
                        }
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                      }}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 3
                    }}>
                    <StringBox 
                      name="Color"
                      value={`${level.color}`} 
                      disabled={disabled}
                      onBlur={(changed: string) => {
                        // TODO: I'll change this to a color picker later
                        const new_obj = obj;
                        const level_finder = new_obj.encumbrance_levels.filter(o => o.true_id === level.true_id);
                        if (level_finder.length === 1) {
                          level_finder[0].color = changed;
                        }
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                      }}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 3
                    }}>
                    <Tooltip title={`Remove Encumbrance Level`}>
                      <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                        disabled={disabled}
                        onClick={ () => {
                          const new_obj = obj;
                          new_obj.encumbrance_levels = new_obj.encumbrance_levels.filter(o => o.true_id !== level.true_id);
                          setObj(new_obj);
                          setReloading(true);
                          setChangeTime(common_util.get_time_number());
                        }}>
                        <DeleteForever/>
                      </Fab>
                    </Tooltip>
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
        :
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox 
              name="Max Level"
              value={`${obj.max_level}`} 
              type="number"
              disabled={disabled}
              onBlur={(changed: string) => {
                const new_obj = obj;
                new_obj.max_level = +changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
        }
      </Grid>
    </FeatureSourceInput>
  ); 
}
